import { defineComponent as _defineComponent } from 'vue'
import { resolveComponent as _resolveComponent, openBlock as _openBlock, createBlock as _createBlock } from "vue"

import { App } from "@src-capacitor/@capacitor/app"
import { Network } from "@src-capacitor/@capacitor/network"
import { SplashScreen } from "@src-capacitor/@capacitor/splash-screen"
import { StatusBar, Style } from "@src-capacitor/@capacitor/status-bar"
import { date, useMeta, useQuasar } from "quasar"
import { csiAPI } from "src/boot/axios"
import { LINKS } from "src/env/links"
import router from "src/router"
import { useAuthStore } from "src/stores/authStore"
import { useCapacitorAppInfoStore } from "src/stores/capacitorAppInfoStore"
import { useCapacitorDeviceInfoStore } from "src/stores/capacitorDeviceInfoStore"
import { useConfigurationStore } from "src/stores/configurationStore"
import { useLocalUserSettingsStore } from "src/stores/localUserSettingsStore"
import { useManagerTopNavigationStore } from "src/stores/managerTopNavigationStore"
import { useNotificationsStore } from "src/stores/notificationsStore"
import { useUnsubmittedWorkStore } from "src/stores/unsubmittedWork"
import { Log } from "src/util/classes/log"
import { STAGNANT_WORK_INTERVAL } from "src/util/constants/configurations"
import { GlobalNamedPath } from "src/util/enums/namedPaths"
import { stagnantWorkDialog, systemDialog } from "src/util/functions/dialogs"
import {
  compareData,
  filterOldNotifications,
  isSystemNotification,
  showConnectionLostNotification,
  showConnectionReestablishedNotification,
} from "src/util/functions/notifications"
import supportedBrowsers from "src/util/functions/supportedBrowsers"
import { getNotificationMockData } from "src/util/mockData/notifications"
import { NotificationCardData } from "src/util/types/notifications"
import { onBeforeMount, onMounted, onUnmounted, watch } from "vue"
import { useRoute } from "vue-router"

// TODO: When vue-i18n is updated, we will be able to use top-level awaits and conditionally import them

//region Variables


export default /*#__PURE__*/_defineComponent({
  __name: 'App',
  setup(__props) {

const $q = useQuasar()
const iconRegex = new RegExp(
  "^fa-(solid|regular|light|thin|duotone|brands) fa-|^fa[srltdbk] fa-|^img:"
)
/**
 * Maps custom keys to image paths
 */
const iconMappings: Record<string, string> = {
  "sfx-logo": "img:img/sfx_logo.svg",
  "sammy-big": "img:img/sammy_big.svg",
  "sammy-small": "img:img/sammy_small.svg",
  "google-logo": "img:img/google_logo.svg",
  "microsoft-logo": "img:img/microsoft_logo.svg",
  "supersede-issue": "img:icons/supersede_issue.svg",
  "radio-on": "img:icons/radio_on.svg",
  "radio-off": "img:icons/radio_off.svg",
  wizard: "img:img/wizard.svg",
  "wizard-white": "img:img/wizard_white.svg",
}
const authStore = useAuthStore()
const managerTopNavigationStore = useManagerTopNavigationStore()
const route = useRoute()
let stagnantWorkInterval: ReturnType<typeof setInterval> | undefined
const unsubmittedWorkStore = useUnsubmittedWorkStore()

//endregion

//region Lifecycle

$q.iconMapFn = (iconName) => {
  // Apply icon mapping if possible
  const mappedIcon = iconMappings[iconName]
  if (mappedIcon) {
    return { icon: `${mappedIcon}` }
  }

  // Prefix names that do not match `iconRegex`
  if (!iconRegex.test(iconName)) {
    return {
      cls: `fa-regular fa-${iconName}`,
    }
  }

  // Images should be handled by the default
  if (iconName.startsWith("img:")) return

  // Otherwise, assume it's a Font Awesome class string
  return { cls: iconName }
}

onBeforeMount(async () => {
  csiAPI.defaults.baseURL = authStore.getCSIBaseUrl

  if (authStore.selectedWorkspace) {
    await unsubmittedWorkStore.updateStagnantWork()
    await unsubmittedWorkStore.updateHasUnsubmittedWork()
  }

  if (!stagnantWorkInterval) {
    stagnantWorkInterval = setInterval(async () => {
      if (authStore.selectedWorkspace) {
        await unsubmittedWorkStore.updateStagnantWork()
      }
    }, STAGNANT_WORK_INTERVAL)
  }

  return
  // TODO: Lets not spam people with notifications
  await fetchNotificationData()
})

onMounted(async () => {
  if (!supportedBrowsers.test(navigator.userAgent)) {
    Log.debug({
      message: "Unsupported Browser used",
      level: "info",
      data: { userAgent: navigator.userAgent },
    })

    if (!useLocalUserSettingsStore().skipUnsupportedBrowser) {
      await router.push({ name: GlobalNamedPath.UNSUPPORTED_BROWSER })
    }
  }
})

useMeta(() => {
  return {
    title:
      managerTopNavigationStore.title.length > 0
        ? "SAMMY | " + managerTopNavigationStore.title
        : "SAMMY",
  }
})

//endregion

//region Capacitor Specific

if (process.env.MODE === "capacitor") {
  //region Variables

  const capacitorAppInfoStore = useCapacitorAppInfoStore()
  const capacitorDeviceInfoStore = useCapacitorDeviceInfoStore()

  //endregion

  //region Lifecycle

  onBeforeMount(async () => {
    const appInfo = await App.getInfo()
    capacitorAppInfoStore.setAppInfo(appInfo)
    await capacitorDeviceInfoStore.setStaticState()

    addAppStateListener()
    addAppRestoredResultListener()
    addAppUrlOpenListener()
    await stagnantWorkHandler()
    addNetworkChangeListener()

    return
    // TODO: Let's not spam people with notifications for now
    await notificationHandler()
  })

  onMounted(async () => {
    // App is ready, so we can remove the splash screen
    await SplashScreen.hide()
  })

  onUnmounted(async () => {
    await App.removeAllListeners()
    await Network.removeAllListeners()
  })

  //endregion

  //region Functions

  function addAppStateListener() {
    void App.addListener("appStateChange", ({ isActive }) => {
      capacitorAppInfoStore.setAppState(isActive)
    })
  }

  // handle any plugin call results that were delivered when your app was not running
  function addAppRestoredResultListener() {
    void App.addListener("appRestoredResult", (data) => {
      capacitorAppInfoStore.setRestoredEvent(data)
    })
  }

  function addAppUrlOpenListener() {
    void App.addListener("appUrlOpen", (event) => {
      // Our URL = app-dev.safeex.cloud
      // slug = /w/2/?testVal="someData"

      try {
        const openedURL = new URL(event.url)
        const path = openedURL.pathname

        if (openedURL.hostname !== LINKS.CAPACITOR_DEEPLINK_HOSTNAME) {
          // Does not come from a trusted website. Failsafe in case the deep linking gets busted.
          Log.error({
            message: "Tried opening deep link with bad hostname",
            data: { event, openedURL },
          })

          return
        }

        if (!authStore.isLoggedIn && path === "/") {
          const code = openedURL.searchParams.get("code")
          authStore.handleAuthorizationCode(code)
        } else {
          Log.debug({ message: "Open with deep link", data: { event, path } })

          // We only push to the route if there is a path present
          if (path) {
            router.push({ path })
          }
        }
      } catch (error) {
        Log.error({ error, message: "Failed to parse deep link URL", data: { event } })
      }
    })
  }

  function addNetworkChangeListener() {
    void Network.addListener("networkStatusChange", (status) => {
      if (!status.connected && capacitorAppInfoStore.hasInternet) {
        showConnectionLostNotification()
        capacitorAppInfoStore.hasInternet = false
      } else if (status.connected && !capacitorAppInfoStore.hasInternet) {
        showConnectionReestablishedNotification()
        capacitorAppInfoStore.hasInternet = true
      }
    })
  }

  async function notificationHandler() {
    const notificationsStore = useNotificationsStore()
    for (let i = 0; i < notificationsStore.sysNotifications.length; i++) {
      let notification = notificationsStore.sysNotifications[i]
      if (!notification.isRead) {
        const userConfirmed = await systemDialog(notification)
        if (userConfirmed) {
          // Update the notification store
          notification.isRead = true
          notificationsStore.count--
          // Update the system notifications in the server
          // TODO: Since the notification page directly use the data from the server, the read status
          //  needs to be updated for the system notifications in the server
          // TODO: Incorporate the patch API for a notification below when available
          // patchNotificationsPartialUpdate(notification)
        }
      }
    }
  }

  async function stagnantWorkHandler() {
    if (unsubmittedWorkStore.isWorkStagnant) {
      const configurationStore = useConfigurationStore()

      if (!configurationStore.shownStagnantWorkDialogDate) {
        await stagnantWorkDialog(unsubmittedWorkStore.oldestWorkInDays)
        configurationStore.shownStagnantWorkDialogDate = Date.now()
      } else {
        const currentDate = Date.now()
        const dateDiff = date.getDateDiff(
          currentDate,
          configurationStore.shownStagnantWorkDialogDate
        )

        if (dateDiff >= 7) {
          configurationStore.shownStagnantWorkDialogDate = undefined
          await stagnantWorkHandler()
        }
      }
    }
  }

  //endregion

  //region Setup Code

  // only works on Android
  void StatusBar.setBackgroundColor({ color: "#E6E8EB" })
  void StatusBar.setStyle({ style: Style.Light })

  //endregion
} else {
  watch(
    () => route.query,
    (queryParams) => {
      if (!authStore.isLoggedIn) {
        // We don't expect arrays, but it is better to have checks
        if (typeof queryParams.code === "string") {
          authStore.handleAuthorizationCode(queryParams.code)
        } else return
      }
    }
  )
}

//endregion

//region Functions

// Fetch number of notifications and system notifications from the server and store them in the store
function fetchNotificationData() {
  try {
    const notificationsStore = useNotificationsStore()
    const notificationsInDB: NotificationCardData[] = getNotificationMockData()
    const validNotifications = notificationsInDB?.filter(
      (notification) => filterOldNotifications(notification) && !notification.isRead
    )
    notificationsStore.count = validNotifications.length
    notificationsStore.sysNotifications = validNotifications
      .filter((notification) => isSystemNotification(notification))
      .sort(compareData)
  } catch (error) {
    Log.error({ error, message: "Failed to fetch the notifications" })
  }
}

//endregion

return (_ctx: any,_cache: any) => {
  const _component_router_view = _resolveComponent("router-view")!

  return (_openBlock(), _createBlock(_component_router_view))
}
}

})